import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  DollarSign, TrendingUp, Building, Wallet, UserPlus, AlertTriangle,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit/PageHeader";
import { StatCard } from "@/components/kit/StatCard";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";

const RANGES = [{ d: 7, label: "7d" }, { d: 30, label: "30d" }, { d: 90, label: "90d" }];

function Donut({ counts }) {
  const segs = [
    { key: "active", color: "#22C55E", label: "Active" },
    { key: "trial", color: "#EAB308", label: "Trial" },
    { key: "expired", color: "#EF4444", label: "Expired" },
  ];
  const total = segs.reduce((s, x) => s + (counts[x.key] || 0), 0) || 1;
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="saas-subscriptions">
      <h3 className="text-sm font-medium text-foreground">Subscriptions</h3>
      <div className="mt-4 flex items-center gap-5">
        <div className="text-3xl font-semibold text-foreground">{fmtNumber(counts.total)}<span className="ml-1 text-xs text-muted-foreground">tenants</span></div>
      </div>
      <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-white/[0.05]">
        {segs.map((s) => (
          <div key={s.key} style={{ width: `${((counts[s.key] || 0) / total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {segs.map((s) => (
          <div key={s.key} className="flex items-center justify-between text-sm" data-testid={`saas-count-${s.key}`}>
            <span className="flex items-center gap-2 text-foreground/90"><span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.label}</span>
            <span className="font-mono text-xs text-muted-foreground">{fmtNumber(counts[s.key] || 0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SaasDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const { data } = useQuery({ queryKey: ["saas-dashboard", days], queryFn: async () => (await http.get(`/saas/dashboard?days=${days}`)).data });
  const cur = data?.currency || "USDT";
  const max = Math.max(...((data?.top_tenants || []).map((t) => t.value)), 1);

  const cards = [
    { label: "MRR", value: `${fmtNumber(data?.mrr)} ${cur}`, icon: DollarSign, accent: "#22C55E" },
    { label: "ARR", value: `${fmtNumber(data?.arr)} ${cur}`, icon: TrendingUp, accent: "#22C55E" },
    { label: "Total Revenue", value: `${fmtNumber(data?.total_revenue)} ${cur}`, icon: Wallet, accent: "#A1A1AA" },
    { label: "Active Tenants", value: fmtNumber(data?.tenants?.active), icon: Building, accent: "#A1A1AA" },
    { label: `New (${days}d)`, value: fmtNumber(data?.new_clients), icon: UserPlus, accent: "#EAB308" },
    { label: "Expiring Soon", value: fmtNumber((data?.expiring || []).length), icon: AlertTriangle, accent: "#EF4444" },
  ];

  return (
    <div className="space-y-6" data-testid="saas-dashboard">
      <div className="flex items-center justify-between">
        <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} subtitle="Your SaaS platform at a glance — subscriptions, revenue & tenant growth." />
        <div className="flex gap-1 rounded-lg border border-white/[0.08] bg-[#141417] p-1">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`saas-range-${r.label}`}
              className={`rounded-md px-3 py-1 text-xs ${days === r.d ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>{r.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((card, i) => (
          <StatCard key={card.label} {...card} delay={i * 40} testId={`saas-stat-${card.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5 lg:col-span-2" data-testid="saas-growth-chart">
          <h3 className="mb-4 text-sm font-medium text-foreground">Subscription growth</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data?.growth || []} margin={{ left: -10, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="grw" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1a1a1e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }} />
              <Area type="monotone" dataKey="total" name="Total tenants" stroke="#22C55E" strokeWidth={2} fill="url(#grw)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <Donut counts={data?.tenants || {}} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="saas-top-tenants">
          <h3 className="text-sm font-medium text-foreground">Top tenants by revenue</h3>
          <div className="mt-4 space-y-3">
            {(data?.top_tenants || []).map((tnt) => (
              <button key={tnt.tenant_id} onClick={() => navigate(`/organizations/${tnt.tenant_id}`)} className="w-full space-y-1 text-left">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate text-foreground/90 hover:text-white">{tnt.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{fmtMoney(tnt.value)}</span>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-white/[0.05]"><div className="h-full rounded-full bg-[#22C55E]/70" style={{ width: `${(tnt.value / max) * 100}%` }} /></div>
              </button>
            ))}
            {(data?.top_tenants || []).length === 0 && <p className="text-sm text-muted-foreground">No revenue yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="saas-recent-tenants">
          <h3 className="mb-3 text-sm font-medium text-foreground">Recent registrations</h3>
          <div className="space-y-1">
            {(data?.recent_registrations || []).map((r) => (
              <button key={r.tenant_id} onClick={() => navigate(`/organizations/${r.tenant_id}`)} className="flex w-full items-center justify-between border-b border-white/[0.05] py-2 text-left text-sm last:border-0">
                <div className="min-w-0"><div className="truncate text-foreground/90">{r.name}</div><div className="text-xs text-muted-foreground">{fmtDate(r.createdAt)}</div></div>
                <StatusBadge status={r.status} />
              </button>
            ))}
            {(data?.recent_registrations || []).length === 0 && <p className="text-sm text-muted-foreground">No tenants yet.</p>}
          </div>
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="saas-expiring">
          <h3 className="mb-3 text-sm font-medium text-foreground">Expiring soon</h3>
          <div className="space-y-1">
            {(data?.expiring || []).map((e) => (
              <button key={e.tenant_id} onClick={() => navigate(`/organizations/${e.tenant_id}`)} className="flex w-full items-center justify-between border-b border-white/[0.05] py-2 text-left text-sm last:border-0">
                <div className="min-w-0"><div className="truncate text-foreground/90">{e.name}</div><div className="text-xs text-muted-foreground">{e.plan_name || "—"}</div></div>
                <span className={`font-mono text-xs ${e.days_left <= 3 ? "text-[#EF4444]" : "text-[#EAB308]"}`}>{e.days_left}d left</span>
              </button>
            ))}
            {(data?.expiring || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing expiring in 7 days.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
